import React, { FC, useEffect, useState } from 'react';

import FactoryIcon from '@mui/icons-material/Factory';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import MenuItem from '@mui/material/MenuItem';
import Menu from '@mui/material/Menu';
import { TextField } from '@mui/material';

import styles from './PowerplantDropdown.module.scss';
import { PowerplantService } from 'services/PowerplantService';
import { useAppDispatch, useAppSelector } from 'store/storeHooks';
import { powerplantActions, PowerplantState } from 'store/powerplant/powerplant-slice';
import { PowerplantModel } from 'models/PowerplantModel';
import { menuBasePaperProps } from 'styles';
import { AlertConfigModel } from 'dialogs/AlertDialog/AlertConfigModel';
import AlertDialog from 'dialogs/AlertDialog/AlertDialog';


interface PowerplantDropdownProps {}

const PowerplantDropdown: FC<PowerplantDropdownProps> = () => {

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [currentAlert, setCurrentAlert] = useState<AlertConfigModel | null>(null);
  const [isAlertDialogOpen, setIsAlertDialogOpen] = useState<boolean>(false);
  const [powerplants, setPowerplants] = useState<PowerplantModel[]>([])
  const [searchQuery, setSearchQuery] = useState('');

  const dispatch = useAppDispatch();
  const powerplant: PowerplantState = useAppSelector(state => state.powerplant);

  const menuOpen = Boolean(anchorEl);

  useEffect(() => {
    const getPowerplants = async () => {
      try {
        const fetchedPowerplants = await PowerplantService.getPowerplants();

        const errorDetails = (fetchedPowerplants as any).detail;
        if (!fetchedPowerplants || errorDetails) {
          throw new Error('Error getting powerplants: ' + errorDetails);
        }

        setPowerplants(fetchedPowerplants);
      } catch (error) {
        console.error('Failed getting powerplants:', error);
        const alertConfig: AlertConfigModel = {title: 'Error', message: 'Failed getting powerplants. Please refresh, and contact support if the issue persists.'};
        setCurrentAlert(alertConfig);
        setIsAlertDialogOpen(true);
      }
    };

    getPowerplants();
  }, []);

  const powerplantSelectHandler = (selectedPowerplant: PowerplantModel | null): void => {
    if (!selectedPowerplant) {
      dispatch(powerplantActions.clear());
    } else {
      dispatch(powerplantActions.set(selectedPowerplant));
    }
    menuCloseHandler();
    setSearchQuery('');
  };

  const menuClickHandler = (event: React.MouseEvent<HTMLElement>): void => {
    setAnchorEl(event.currentTarget);
  };

  const menuCloseHandler = (): void => {
    setAnchorEl(null);
  };

  const filteredPowerplants = powerplants.filter(powerplant =>
    powerplant.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const powerplantMenuItems = (
    <div>
      <TextField
        variant="outlined"
        size="small"
        placeholder="Search..."
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.target.value)}
        style={{ margin: '10px' }}
      />

      <MenuItem
        onClick={() => powerplantSelectHandler(null)}
        style={{backgroundColor: !powerplant._id ? '#51bdc3' : ''}}
      >
        None
      </MenuItem>
      
      {filteredPowerplants.map((plant) => (
        <MenuItem
          key={plant._id}
          onClick={() => powerplantSelectHandler(plant)}
          style={{backgroundColor: plant._id === powerplant._id ? '#51bdc3' : ''}}
        >
          {plant.name}
        </MenuItem>
      ))}
    </div>
  );

  const powerplantDropdownMenu = (
    <Menu
      anchorEl={anchorEl}
      open={menuOpen}
      onClose={menuCloseHandler}
      PaperProps={menuBasePaperProps}
      transformOrigin={{ horizontal: 'right', vertical: 'top' }}
      anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
    >
      {powerplantMenuItems}
    </Menu>
  );

  return (
    <div
      data-testid="PowerplantDropdown"
      className={styles.PowerplantDropdown}>
      <div className={styles.powerplantDropdown} onClick={menuClickHandler}>
        <FactoryIcon fontSize={'large'} />
        <div className={styles.dropdownLabel}>
          {powerplant.name || 'Select Powerplant'}
          { !menuOpen
            ? <KeyboardArrowDownIcon />
            : <KeyboardArrowUpIcon />
          }
        </div>
      </div>
      { powerplantDropdownMenu }

      { isAlertDialogOpen && currentAlert &&
        <AlertDialog
          onClose={() => {
            setIsAlertDialogOpen(false);
            setCurrentAlert(null);
          }}
          title={currentAlert.title}
          message={currentAlert.message}
        ></AlertDialog>
      }
    </div>
  );
}

export default PowerplantDropdown;
